// TRANSLATION: pending native review
//
// Flemish (nl-BE) catalog for the intake widget. Machine-drafted Flemish —
// acceptable for the build, NOT for launch. A named native Flemish reviewer must
// sign off on the consent wording, honesty-guardrail (scan preview) copy, and
// legal strings before the Belgian sales campaign, per the Translation QA gate in
// SPECS/patient-path-localization.md.
//
// Honesty guardrail (framing 4a): the scan-preview strings below are strictly
// *preparation* language ("Je foto lezen / voorbereiden") — they must never claim
// the personalised report or results are ready. The genuine analysis runs
// server-side after submit and is delivered by email.

import type { Catalog } from './index';

export const nlBE: Catalog = {
  // ── Shared ────────────────────────────────────────────────────────────────
  'common.continue': 'Doorgaan',
  'common.theClinic': 'de kliniek',

  // ── Launcher pill + modal shell (EmbedShell) ────────────────────────────────
  'launcher.label': 'Ontvang je selfie-huidrapport',
  'launcher.ariaOpen': 'Open huidrapport-intake — ontvang je selfie-huidrapport',
  'shell.dialogAria': 'Huidrapport-intake',
  'shell.closeAria': 'Intake sluiten',

  // ── Aperture / config states (Widget) ───────────────────────────────────────
  'aperture.label': 'Je rapport voorbereiden',
  'aperture.sub': 'Het instrument warmt op',
  'config.error': 'Kan de kliniekgegevens niet laden. Ververs de pagina.',

  // ── aria-live step announcements (Widget) ───────────────────────────────────
  'aria.toProfile': 'Naar stap 2 van 6: Jouw profiel',
  'aria.toPhoto': 'Naar stap 3 van 6: Jouw foto',
  'aria.toPreparing': 'Naar stap 4 van 6: Je rapport voorbereiden',
  'aria.toContact': 'Naar stap 5 van 6: Waar je rapport naartoe sturen',
  'aria.toSubmitting': 'Stap 6 van 6: Je aanvraag versturen',
  'aria.submitted': 'Je aanvraag is verzonden. Je rapport komt per e-mail.',
  'aria.backToConsent': 'Terug naar stap 1 van 6: Toestemmingsscherm',
  'aria.backToDetails': 'Terug naar stap 2 van 6: Je gegevens',
  'aria.backToPhoto': 'Terug naar stap 3 van 6: Jouw foto',
  'aria.backToContact': 'Terug naar stap 5 van 6: Je contactgegevens',
  'aria.closeSubmitted': 'Je rapportaanvraag is verzonden. Je rapport komt per e-mail.',

  // ── Stage 01 — Consent ──────────────────────────────────────────────────────
  'consent.constellation.armed': 'Instrument gereed',
  'consent.constellation.begin': 'Hier begint jouw profiel',
  'consent.title.line1': 'Jouw persoonlijke',
  'consent.title.line2Pre': 'huid',
  'consent.title.line2Em': 'rapport',
  'consent.sub':
    'Beantwoord een paar korte vragen en voeg een optionele selfie toe. We stellen een persoonlijk huidrapport op en sturen het binnen enkele minuten naar je inbox.',
  'consent.formAria': 'Toestemming voor persoonlijk huidrapport',
  'consent.privacy':
    'Je foto en antwoorden worden alleen gebruikt om je rapport op te stellen en op je aanvraag te reageren. Nooit verkocht.',
  'consent.agree':
    'Ik ga akkoord met het verzamelen en gebruiken van mijn foto en antwoorden voor dit persoonlijke huidrapport. ',
  'consent.privacyLink': 'Privacybeleid \u2197',
  'consent.desc': 'Je moet akkoord gaan voordat je verdergaat.',
  'consent.cta': 'START MIJN RAPPORT',
  'consent.ctaHint': 'Vink het toestemmingsvakje hierboven aan om deze knop te activeren.',
  'consent.legal':
    'Door verder te gaan, bevestig je dat je 18 jaar of ouder bent. Dit rapport biedt cosmetische begeleiding en vervangt geen professioneel medisch advies.',

  // ── Stage 02 — Profile + Q1–Q4 ──────────────────────────────────────────────
  'profile.backToConsentAria': 'Ga terug naar toestemming',
  'profile.prevQuestionAria': 'Ga naar de vorige vraag',
  'profile.title.pre': 'Over ',
  'profile.title.em': 'jouw',
  'profile.title.post': ' huid',
  'profile.sub': 'Een paar details helpen om je rapport te personaliseren.',
  'profile.ageLabel': 'Leeftijdscategorie',
  'profile.required': '*',
  'profile.optional': 'optioneel',
  'profile.ageAria': 'Leeftijdscategorie',
  'profile.skinTypeLabel': 'Huidtype',
  'profile.skinTypeAria': 'Huidtype',
  'profile.concernsLabel': 'Huidproblemen',
  'profile.concernsHelp': 'Selecteer alles wat van toepassing is',
  'profile.concernsAria': 'Huidproblemen',
  'profile.ageRanges': ['18\u201324', '25\u201334', '35\u201344', '45\u201354', '55+'],
  'profile.skinTypes': ['Droog', 'Vet', 'Gemengd', 'Normaal', 'Gevoelig'],
  'profile.skinConcerns': [
    'Puistjes', 'Droogheid', 'Oneffen huidtint', 'Fijne lijntjes',
    'Donkere vlekjes / pigmentvlekken', 'Gevoeligheid', 'Roodheid', 'Doffe huid', 'Andere',
  ],

  'q.step.profile': 'Stap 2 van 6 — Jouw profiel',
  'q.step.q1': 'Vraag 1 van 4',
  'q.step.q2': 'Vraag 2 van 4',
  'q.step.q3': 'Vraag 3 van 4',
  'q.step.q4': 'Vraag 4 van 4',

  'q1.prompt': 'Wat zou je het liefst aan je huid willen verbeteren?',
  'q2.prompt': 'Welk resultaat zou je het liefst zien?',
  'q3.prompt': 'Heb je eerder esthetische behandelingen gehad?',
  'q4.prompt': 'Welk investeringsbereik voelt comfortabel?',
  'q4.helper': 'Zo kunnen we realistische opties voorstellen.',

  'q1.options': [
    'Fijne lijntjes of rimpels',
    'Donkere vlekjes of oneffen huidtint',
    'Doffe of vermoeide huid',
    'Puistjes of oneffen textuur',
    'Vroege verouderingstekenen voorkomen',
  ],
  'q2.options': [
    'Een gladdere huid',
    'Een stralendere, meer glanzende huid',
    'Een meer uitgeruste, frisse look',
    'Vollere lippen of een betere gelaatsbalans',
    'Een gezonder ogende huid',
  ],
  'q3.options': ['Nee, nog niet', 'Een of twee keer', 'Af en toe', 'Regelmatig'],
  'q4.options': ['Onder \u20AC300', '\u20AC300\u2013\u20AC1.000', '\u20AC1.000+'],

  // ── Stage 03 — Photo ────────────────────────────────────────────────────────
  'photo.backAria': 'Ga terug naar de gegevens',
  'photo.step': 'Stap 3 van 6 — Jouw foto',
  'photo.title.usePhoto': 'Deze foto gebruiken?',
  'photo.title.reading': 'Je foto lezen',
  'photo.title.default': 'Een heldere blik op je huid',
  'photo.sub.camera': 'Zorg dat je gezicht scherp en gelijkmatig belicht is.',
  'photo.sub.upload': 'Even stilhouden — gezichtspunten in kaart brengen.',
  'photo.sub.showCamera':
    'Goede belichting helpt ons fijne details te lezen. Je foto wordt alleen gebruikt om je rapport op te stellen.',
  'photo.sub.default':
    'Kies een recente, goed belichte foto van je gezicht. Ze wordt \u00e9\u00e9n keer gebruikt, alleen voor je rapport.',
  'photo.status.waiting': 'Wachten op cameratoegang\u2026',
  'photo.status.ready': 'Camera gereed',
  'photo.status.denied': 'Cameratoegang geweigerd — upload in plaats daarvan een foto',
  'photo.status.none': 'Geen camera gevonden — upload in plaats daarvan een foto',
  'photo.cue.align': 'Lijn je gezicht uit binnen de contour',
  'photo.cue.allow': 'Sta cameratoegang toe om te beginnen',
  'photo.upload.aria': 'Kies een foto van je toestel',
  'photo.upload.tap': 'Tik om een foto te kiezen',
  'photo.upload.formats': 'JPG of PNG, tot 10 MB',
  'photo.error.format': 'Kies een JPG- of PNG-afbeelding.',
  'photo.error.size': 'De afbeelding mag maximaal 10 MB groot zijn.',
  'photo.previewAlt': 'Voorbeeld van je selfie',
  'photo.captureAria': 'Foto maken',
  'photo.retake': 'Foto opnieuw maken',
  'photo.chooseDifferent': 'Kies een andere foto',
  'photo.seg.aria': 'Methode voor foto-invoer',
  'photo.seg.camera': 'Camera',
  'photo.seg.upload': 'Uploaden',
  'photo.skip': 'Doorgaan zonder selfie →',
  'photo.skipAria': 'Doorgaan zonder selfie',

  // ── Stage 04 — Scan preview (preparation framing 4a) ────────────────────────
  'scan.step': 'Je rapport voorbereiden',
  'scan.p0.titlePre': 'Je foto ',
  'scan.p0.titleEm': 'lezen',
  'scan.p0.narration': 'Je foto wordt op dit toestel voorbereid.',
  'scan.p1.titlePre': 'Je huidrapport ',
  'scan.p1.titleEm': 'voorbereiden',
  'scan.p1.narration': 'Gezichtspunten in kaart brengen om je rapport te personaliseren.',
  'scan.p2.titlePre': 'Bijna ',
  'scan.p2.titleEm': 'klaar',
  'scan.p2.narration': 'Nog \u00e9\u00e9n laatste stap om je rapport te versturen.',
  'scan.layers': ['Tint', 'Textuur', 'Detail'],
  'scan.foot': 'Je rapport wordt priv\u00e9 voorbereid en na de volgende stap verstuurd.',
  'scan.live': 'Je rapport wordt op je toestel voorbereid. Nog \u00e9\u00e9n laatste stap om je rapport te versturen.',

  // ── Stage 05 — Contact ──────────────────────────────────────────────────────
  'contact.backAria': 'Ga terug naar je foto',
  'contact.step': 'Stap 5 van 6 — Waar sturen we het naartoe',
  'contact.title.line1': 'Waarheen sturen we',
  'contact.title.line2Pre': 'je ',
  'contact.title.line2Em': 'rapport?',
  'contact.sub': 'Je rapport wordt priv\u00e9 opgesteld en verstuurd.',
  'contact.formAria': 'Je contactgegevens',
  'contact.firstName': 'Voornaam',
  'contact.email': 'E-mail',
  'contact.phone': 'Telefoon',
  'contact.firstNamePlaceholder': 'Je voornaam',
  'contact.emailPlaceholder': 'jij@voorbeeld.com',
  'contact.phonePlaceholder': 'Voer je nummer in',
  'contact.emailHelp': 'Je rapport komt hier aan.',
  'contact.error.firstName': 'Voornaam is verplicht',
  'contact.error.email': 'Controleer het e-mailadres — zonder dit kan je rapport je niet bereiken.',
  'contact.error.phone': 'Voer een geldig telefoonnummer in',
  'contact.countryCodeAria': 'Landcode',
  'contact.cta': 'VRAAG MIJN RAPPORT AAN',
  'contact.legal':
    'Door te verzenden ga je ermee akkoord dat {brand} contact met je mag opnemen via e-mail of sms over je rapport en afspraakopties. Bericht-/datatarieven kunnen van toepassing zijn. Antwoord STOP om je af te melden.',

  // ── Stage 06 — Submitting ───────────────────────────────────────────────────
  'submit.step': 'Stap 6 van 6 — Je aanvraag versturen',
  'submit.title.pre': 'Je aanvraag ',
  'submit.title.em': 'veilig versturen',
  'submit.narration': 'Je aanvraag wordt veilig verstuurd.',
  'submit.foot': 'Priv\u00e9 voorbereid en naar je inbox verstuurd.',
  'submit.error.4xx': 'Er ging iets mis. Controleer je gegevens en probeer het opnieuw.',
  'submit.error.review': 'Controleer je gegevens',
  'submit.error.network': 'We hebben verbindingsproblemen. Je gegevens zijn veilig.',
  'submit.error.tryAgain': 'Opnieuw proberen',
  'submit.error.cap': 'Je hebt de demolimiet van vandaag bereikt. Probeer het morgen opnieuw.',
  'submit.live': 'Je aanvraag wordt veilig verstuurd. Priv\u00e9 voorbereid en naar je inbox verstuurd.',

  // ── Stage 07 — Success ──────────────────────────────────────────────────────
  'success.live':
    'Je rapportaanvraag is succesvol verzonden. Je persoonlijke huidrapport wordt binnenkort naar je e-mailadres gestuurd.',
  'success.title.line1': 'Je rapport is',
  'success.title.em': 'onderweg',
  'success.sub.pre': 'We hebben een bevestiging gestuurd naar ',
  'success.sub.post': '. Je persoonlijke huidrapport volgt binnen enkele minuten.',
  'success.talk': 'Liever even persoonlijk overleggen?',
  'success.book': 'Spreek ons team →',
  'success.bookAria': 'Boek een consultatie bij {brand} (opent in nieuw tabblad)',
  'success.help': 'Iets onduidelijk? ',
  'success.contactLink': 'Neem contact op',
  'success.emailAria': 'E-mail {brand}',
  'success.barStatus': 'Rapport verstuurd',
};
